import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { IGenericVehicle } from '@cbgms/api/modules/vehicles'
import { UpdateExternalIdDialogWithoutForm } from '../Details/External/components/UpdateExternalIdDialog'
import { VehicleProvider } from '@cbgms/api'
import { useDefaultOrderAccountVehicleProvider } from 'app/vehicles/helpers/use-default-order-account-vehicle-provider'
import { SelectVehicleFunc } from './types.d'
import { useReduxRefsContext } from 'app/ReduxRefsContext'
import { SelectVehicleTypeContext } from 'app/vehicles/components/SelectVehicleType/SelectVehicleTypeContext'
interface State {
  types?: IGenericVehicle[]
  resolve?: (value?: any | PromiseLike<any>) => void
  reject?: (reason?: any) => void
  provider: VehicleProvider
  promise?: PromiseLike<any>
}

const initialState = {
  types: undefined,
  resolve: undefined,
  reject: undefined,
  // Todo fetch country default
  provider: VehicleProvider.Hbase
}

// SelectVehicleType is a context that will expose a function that triggers a selection modal for users to select a Ktype/HaynesID/etc.
// when calling showSelection, we return a promise that will resolve once a selection is made OR reject when cancelled/closed.
const _SelectVehicleType: React.FC<{}> = ({ children }) => {
  const [state, setState] = useState<State>(initialState)
  const [promise, setPromise] = useState<Promise<IGenericVehicle> | null>(null)
  const defaultProvider = useDefaultOrderAccountVehicleProvider()
  const cancel = useCallback(() => state.reject && state.reject(), [state.reject])
  const showSelection = useCallback<SelectVehicleFunc>(
    async (types, provider): Promise<IGenericVehicle> => {
      if (types.length === 1) {
        return Promise.resolve(types[0])
      }
      // Wait for previous selection to finish
      try {
        await promise
      } catch {
        // cancelled
      }
      const p = new Promise<IGenericVehicle>((resolve, reject) => {
        setState({
          types,
          provider: provider || defaultProvider,
          resolve: type => {
            setState(initialState)
            resolve(type)
          },
          reject: () => {
            setState(initialState)
            reject('no-selection-made')
          }
        })
      })
      setPromise(p)
      return p
    },
    [promise]
  )

  // we need to link this function by ref, essentially we're passing this function up to use it in redux middleware
  const { setShowVehicleSelectionFunc } = useReduxRefsContext()
  useEffect(() => {
    setShowVehicleSelectionFunc(showSelection)
  }, [setShowVehicleSelectionFunc, showSelection])

  const contextValue = useMemo(() => ({ showSelection, hasBeenProvided: true }), [showSelection])
  return (
    <SelectVehicleTypeContext.Provider value={contextValue}>
      {state.types && (
        <UpdateExternalIdDialogWithoutForm
          provider={state.provider}
          open={state.types !== undefined}
          onClose={cancel}
          onVehicleSelect={state.resolve}
          translationKeyForHeader='vehicle.refine-vehicle-title'
          vehicleSuggestions={state.types}
        />
      )}
      {children}
    </SelectVehicleTypeContext.Provider>
  )
}

const SelectVehicleType: React.FC = memo(_SelectVehicleType)
export default SelectVehicleType
